'use client';

import Link from 'next/link';
import {
  useId,
  useSyncExternalStore,
  type ComponentPropsWithoutRef,
  type MouseEvent,
} from 'react';
import { isInternalRoute, isOrderFlowHref, isTdcCoverageMapHref, resolveCoverageHref } from '@/lib/links';
import {
  beginOrderFlowNavigation,
  getOrderFlowPending,
  subscribeOrderFlowPending,
} from '@/lib/orderFlowNavigation';

type SmartLinkProps = ComponentPropsWithoutRef<'a'> & {
  href: string;
  /** Forwarded to next/link for internal routes; ignored for plain anchors. */
  prefetch?: boolean;
};

function isModifiedClick(event: MouseEvent) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

function subscribeHydrated() {
  return () => {};
}

function getHydratedSnapshot() {
  return true;
}

function getHydratedServerSnapshot() {
  return false;
}

function OrderFlowSpinner() {
  return (
    <svg
      className="block size-4 shrink-0 self-center"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      data-order-flow-spinner
    >
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <circle
        className="order-flow-spinner-arc"
        cx="8"
        cy="8"
        r="6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/**
 * Renders a next/link for internal Next.js routes and a plain `<a>` for
 * proxied legacy paths (e.g. /bestil) and external/non-http links.
 *
 * This avoids the RSC prefetch (`?_rsc=`) that next/link fires for same-origin
 * hrefs, which fails CORS for Mobile Partners proxied paths. See lib/links.ts.
 *
 * For `/bestil` (order flow) links, the first click shows a spinner and locks
 * every other order-flow CTA so a repeat click cannot abort the in-flight
 * document request and start over.
 */
export default function SmartLink({
  href: rawHref,
  prefetch,
  children,
  onClick,
  className,
  target: rawTarget,
  ...rest
}: SmartLinkProps) {
  // Links til TDC's dækningskort peges om til vores eget (se lib/links.ts) og åbnes i samme vindue.
  const href = resolveCoverageHref(rawHref);
  const target = isTdcCoverageMapHref(rawHref) ? undefined : rawTarget;
  const sourceId = useId();
  const pending = useSyncExternalStore(
    subscribeOrderFlowPending,
    getOrderFlowPending,
    getOrderFlowPending,
  );
  const locksOrderFlow = isOrderFlowHref(href) && target !== '_blank';
  const hydrated = useSyncExternalStore(
    subscribeHydrated,
    getHydratedSnapshot,
    getHydratedServerSnapshot,
  );
  // True only after hydration, when the click lock is actually wired. E2E waits for this.
  const orderFlowReady = locksOrderFlow && hydrated;
  const isBusy = locksOrderFlow && pending.pending;
  const isSource = isBusy && pending.sourceId === sourceId;

  function handleOrderFlowClick(event: MouseEvent<HTMLAnchorElement>) {
    if (isModifiedClick(event)) {
      onClick?.(event);
      return;
    }

    if (pending.pending || !beginOrderFlowNavigation(sourceId)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    onClick?.(event);
  }

  const pendingClassName = isBusy
    ? [
        className,
        'pointer-events-none cursor-wait items-center overflow-visible',
        isSource ? 'gap-2' : 'opacity-60',
      ]
        .filter(Boolean)
        .join(' ')
    : className;

  const content = (
    <>
      {isSource && <OrderFlowSpinner />}
      {children}
      {isSource && <span className="sr-only">Åbner bestilling</span>}
    </>
  );

  if (isInternalRoute(href)) {
    return (
      <Link href={href} prefetch={prefetch} target={target} className={className} onClick={onClick} {...rest}>
        {children}
      </Link>
    );
  }

  return (
    <a
      {...rest}
      href={href}
      target={target}
      className={pendingClassName}
      onClick={locksOrderFlow ? handleOrderFlowClick : onClick}
      aria-busy={isBusy || undefined}
      aria-disabled={isBusy || undefined}
      data-order-flow-pending={isBusy ? 'true' : undefined}
      data-order-flow-ready={orderFlowReady ? 'true' : undefined}
      tabIndex={isBusy ? -1 : rest.tabIndex}
    >
      {locksOrderFlow ? content : children}
    </a>
  );
}
